import { formatCurrency } from '@angular/common';
import { Injectable } from '@angular/core';
import { IbanValidity } from '@app/core/payment/iban.service';
import { hasDateRangeError } from '@app/next/declarative-forms/validation/validators/date-range.validator';
import {
  hasE164PhoneFormatError,
  phoneNumberStartsWithZeroError,
} from '@app/next/declarative-forms/validation/validators/phone.validator';
import {
  hasEmailError,
  hasPatternError,
  hasRequiresAnyError,
} from '@fmnts/common/forms';
import { TaxIdValidity } from '@fmnts/common/tax';
import {
  IValidationMessageBuilder,
  ValidationMessageOpts,
  isFieldDeclaration,
  isFormInputField,
} from '@fmnts/declarative-forms';
import {
  hasConditionalMinError,
  hasCustomValidationMessage,
  hasMaxLengthError,
  hasMinLengthError,
  hasRequiredError,
} from '@fmnts/declarative-forms/common';
import { hasTaxIdError } from '@fmnts/declarative-forms/tax';
import { DateService, TranslationService } from '@fmnts/i18n';

/**
 * Validation Message Builder for Next forms.
 */
@Injectable()
export class NextValidationMessageBuilderService
  implements IValidationMessageBuilder
{
  constructor(
    private translationService: TranslationService,
    private dateService: DateService,
  ) {}

  getMessage(
    errors: { [key: string]: any },
    opts: ValidationMessageOpts,
  ): string | null {
    const { formatting, field, declaration } = opts;
    if (!isFormInputField(field)) {
      return null;
    }

    // Do this manually to have an ordering of importance
    if (hasRequiredError(errors)) {
      return this.translationService.getTranslation('field_error_required');
    }
    if (hasEmailError(errors)) {
      return this.translationService.getTranslation('field_error_email');
    }
    if (hasMinLengthError(errors)) {
      return this.translationService.getTranslation(
        'field_error_length_min',
        errors.minlength.requiredLength,
      );
    }
    if (hasMaxLengthError(errors)) {
      return this.translationService.getTranslation(
        'field_error_length_max',
        errors.maxlength.requiredLength,
      );
    }
    if (errors.cbu) {
      switch (errors.cbu) {
        case 'length':
          return this.translationService.getTranslation(
            'field_error_cbu_length',
          );
        case 'bank':
          return this.translationService.getTranslation('field_error_cbu_bank');
        case 'account':
          return this.translationService.getTranslation(
            'field_error_cbu_account',
          );
      }
    }
    // Error messages for phone validation
    if (hasE164PhoneFormatError(errors)) {
      return this.translationService.getTranslation(
        'field_error_phone_invalid',
      );
    }

    if (phoneNumberStartsWithZeroError(errors)) {
      return this.translationService.getTranslation(
        'field_error_phone_starts_with_zero',
      );
    }

    // Error messages for (conditional) min
    if (hasConditionalMinError(errors)) {
      const { conditionalMin } = errors;
      if (conditionalMin.message) {
        return this.translationService.getInlineTranslation(
          conditionalMin.message,
          formatCurrency(
            conditionalMin.min,
            formatting.locale,
            formatting.currency,
          ),
        );
      }
      return this.translationService.getTranslation(
        'field_error_min',
        formatCurrency(
          conditionalMin.min,
          formatting.locale,
          formatting.currency,
        ),
      );
    }
    if (errors.min) {
      return this.translationService.getTranslation(
        'field_error_min',
        formatCurrency(errors.min.min, formatting.locale, formatting.currency),
      );
    }
    if (errors.max) {
      return this.translationService.getTranslation(
        'field_error_max',
        formatCurrency(errors.max.max, formatting.locale, formatting.currency),
      );
    }
    if (hasRequiresAnyError(errors)) {
      const labels = errors.any.map((path) =>
        this.translationService.getInlineTranslation(
          declaration.findByKey(`${path[path.length - 1]}`, isFieldDeclaration)
            ?.label ?? '',
        ),
      );
      return this.translationService.getTranslation(
        'field_error_any',
        labels.join(', '),
      );
    }
    if (hasDateRangeError(errors)) {
      const dateValidator = field.widgetOptions.validators.find(
        (v) => v.name === 'dateRange',
      );
      switch (errors.dateRange.error) {
        case 'invalid':
          return this.translationService.getTranslation(
            'field_error_date_invalid',
          );
        case 'min':
          const minDate = this.dateService.moment(errors.dateRange.min);
          return dateValidator.messages?.min
            ? this.translationService.getInlineTranslation(
                dateValidator.messages.min,
                minDate.format('L'),
              )
            : this.translationService.getTranslation(
                'field_error_date_range_min',
                minDate.format('L'),
              );
        case 'max':
          const maxDate = this.dateService.moment(errors.dateRange.max);
          return dateValidator.messages?.max
            ? this.translationService.getInlineTranslation(
                dateValidator.messages.max,
                maxDate.format('L'),
              )
            : this.translationService.getTranslation(
                'field_error_date_range_max',
                maxDate.format('L'),
              );
      }
    }
    // Error messages for pattern validation
    if (hasPatternError(errors)) {
      if (hasCustomValidationMessage(errors.pattern)) {
        return this.translationService.getInlineTranslation(
          errors.pattern.message,
        );
      }
      return this.translationService.getTranslation('field_error_pattern');
    }
    // Error messages for IBAN validation
    if (errors.iban) {
      switch (errors.iban) {
        case IbanValidity.UnsupportedCountry:
          return this.translationService.getTranslation(
            'field_error_iban_unsupported_country',
          );
        default:
          return this.translationService.getTranslation(
            'field_error_iban_general',
          );
      }
    }
    // Error messages for Sort Code validation
    if (errors.bankAccountSortCode) {
      return this.translationService.getTranslation(
        'field_error_bank_account_sort_code_invalid',
      );
    }
    if (errors.bankAccountNumber) {
      return this.translationService.getTranslation(
        'field_error_bank_account_number_invalid',
      );
    }
    // Error message for addresses
    if (errors.countryUnsupported) {
      return this.translationService.getTranslation(
        'field_error_address_unsupported_country',
      );
    }
    // Error message for Unsupported Bank Codes
    if (errors.unsupportedBankCode) {
      return this.translationService.getTranslation(
        'field_error_iban_unsupported_bank_code',
      );
    }

    const taxErrorMsg = this._getTaxRelatedErrorMessage(errors, opts);
    if (taxErrorMsg) {
      return taxErrorMsg;
    }

    return null;
  }

  private _getTaxRelatedErrorMessage(
    errors: { [key: string]: any },
    opts: ValidationMessageOpts,
  ): string | void {
    if (!hasTaxIdError(errors)) {
      return;
    }

    const { reason } = errors.taxId;
    switch (reason) {
      case TaxIdValidity.MismatchAdditionalData:
        return this.translationService.getTranslation(
          'field_error_tax_id_mismatches_with_other_data',
        );
      case TaxIdValidity.InvalidCharacters:
      case TaxIdValidity.InvalidChecksum:
      case TaxIdValidity.InvalidLength:
      case TaxIdValidity.Unknown:
      default:
        return this.translationService.getTranslation(
          'field_error_tax_id_invalid',
        );
    }
  }
}
